import {
  Button,
  Divider,
  Menu,
  type MenuItem,
  type MenuOption,
  Textarea,
  Tooltip,
} from "@aurem/ui";
import {
  ChevronDown,
  Database,
  MessageCircleQuestionMark,
  MoveUp,
  Square,
} from "lucide-react";
import { useI18n } from "providers/I18nProvider";
import type { FC } from "react";
import { useMemo } from "react";

import { AssistantModeEnum } from "types";

type ModeOption = MenuOption & {
  id: AssistantModeEnum;
};

export const ChatInput: FC<{
  draft: string;
  setDraft: (val: string) => void;
  selectedMode: AssistantModeEnum;
  setSelectedMode: (mode: AssistantModeEnum) => void;
  isThinking: boolean;
  disabled?: boolean;
  onSend: (text: string, mode: AssistantModeEnum) => void;
  onStop: () => void;
}> = ({
  draft,
  setDraft,
  selectedMode,
  setSelectedMode,
  isThinking,
  onSend,
  onStop,
  disabled,
}) => {
  const { t } = useI18n();

  const modes = useMemo<ModeOption[]>(
    () => [
      {
        id: AssistantModeEnum.AuremAgent,
        text: t("apps.admin.chatAI.ui.modeAuremAgent"),
        icon: <Database />,
      },
      {
        id: AssistantModeEnum.General,
        text: t("apps.admin.chatAI.ui.modeGeneralInquiries"),
        icon: <MessageCircleQuestionMark />,
      },
    ],
    [t],
  );

  const menuOptions = useMemo<MenuItem[]>(() => modes, [modes]);

  const handleSend = () => {
    const trimmedDraft = draft.trim();
    if (trimmedDraft) {
      onSend(trimmedDraft, selectedMode);
    }
  };

  return (
    <div
      className="flex flex-col rounded-xl border border-neutral-500/10 bg-white
                 focus-within:outline focus-within:outline-2 focus-within:outline-primary-500
                 focus-within:outline-offset-0  overflow-hidden"
    >
      <div className="max-h-[200px] overflow-y-auto">
        <Textarea
          value={draft}
          onChange={(value) => setDraft(value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !isThinking) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder={t("apps.admin.chatAI.ui.inputPlaceholder")}
          rows={2}
          autosize
          className="!border-0 !outline-none focus:!ring-0 focus:!shadow-none"
          textareaClassName="resize-none"
        />
      </div>
      <Divider />
      <div className="flex items-center justify-between p-2">
        <Menu
          placement="top-start"
          options={menuOptions}
          onSelect={(id) => setSelectedMode(id as AssistantModeEnum)}
        >
          <Button
            variant="ghost"
            color="neutral"
            size="sm"
            startIcon={modes.find((m) => m.id === selectedMode)?.icon}
            endIcon={<ChevronDown />}
          >
            {modes.find((m) => m.id === selectedMode)?.text}
          </Button>
        </Menu>

        {isThinking ? (
          <Tooltip
            contentClassName="p-2"
            content={t("apps.admin.chatAI.ui.stopResponse")}
          >
            <Button onClick={onStop} className="!p-3">
              <Square size={16} />
            </Button>
          </Tooltip>
        ) : (
          <Button
            onClick={handleSend}
            disabled={disabled || !draft.trim()}
            className="!p-3"
          >
            <MoveUp size={16} />
          </Button>
        )}
      </div>
    </div>
  );
};
